import { useState, useRef, RefObject } from 'react'
import { Toast } from 'primereact/toast'
import { 
  bulkReassignRejectedItems,
  transformReassignmentResponse,
  extractOrderIds,
  BulkReassignmentResponse,
  ProcessedItem,
  ProcessedOrder
} from '../services/bulkReassignmentService'
import { ShippingOrder } from '../DeShippingTableWrapper'

type ProcessingState = 'idle' | 'processing' | 'completed' | 'error'

interface UseBulkRejectedItemsReassignmentReturn {
  // State
  isProcessing: boolean
  processingState: ProcessingState
  reassignmentResults: BulkReassignmentResponse | null
  apiResponse: BulkReassignmentResponse | null // Alias for reassignmentResults
  transformedResults: {
    completedItems: ProcessedItem[]
    failedItems: ProcessedItem[]
    processedOrders: ProcessedOrder[]
  } | null
  toast: RefObject<Toast>
  error: string | null
  
  // Actions
  executeReassignment: (selectedOrders: ShippingOrder[]) => Promise<void>
  resetState: () => void
}

export const useBulkRejectedItemsReassignment = (
  ordersRefresh: () => void,
  clearSelection: () => void
): UseBulkRejectedItemsReassignmentReturn => {
  const [isProcessing, setIsProcessing] = useState(false)
  const [processingState, setProcessingState] = useState<ProcessingState>('idle')
  const [reassignmentResults, setReassignmentResults] = useState<BulkReassignmentResponse | null>(null)
  const [transformedResults, setTransformedResults] = useState<{
    completedItems: ProcessedItem[]
    failedItems: ProcessedItem[]
    processedOrders: ProcessedOrder[]
  } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const toast = useRef<Toast>(null)

  const executeReassignment = async (selectedOrders: ShippingOrder[]) => {
    setIsProcessing(true)
    setProcessingState('processing')
    setError(null)
    setReassignmentResults(null)
    setTransformedResults(null)
    
    try {
      // Extract order IDs from selected orders
      const orderIds = extractOrderIds(selectedOrders)
      
      if (orderIds.length === 0) {
        throw new Error('No orders selected for reassignment')
      }

      // Call the API
      const response = await bulkReassignRejectedItems(orderIds)
      
      // Store raw response regardless of success status
      setReassignmentResults(response)
      
      // Transform for UI display
      const transformed = transformReassignmentResponse(response)
      setTransformedResults(transformed)
      
      // Set final state - always completed to show results
      setProcessingState('completed')
      
      // Show appropriate toast based on success and results
      if (response.success) {
        const hasFailures = response.summary.failed_reassignments > 0
        toast.current?.show({
          severity: hasFailures ? 'warn' : 'success',
          summary: hasFailures ? 'Reassignment Completed with Issues' : 'Reassignment Successful',
          detail: `Processed ${response.summary.successful_reassignments} items successfully${hasFailures ? `, ${response.summary.failed_reassignments} failed` : ''}`,
          life: 5000
        })
        
        // On success, silently refresh data and clear selection
        ordersRefresh()
        clearSelection()
      } else {
        // API returned success: false but still has results to show
        toast.current?.show({
          severity: 'error',
          summary: 'Reassignment Failed',
          detail: `All ${response.summary.failed_reassignments} items failed to reassign. See details in Failed tab.`,
          life: 8000
        })
        
        // Still refresh orders data to reflect any changes
        ordersRefresh()
      }
      
    } catch (error) {
      console.error('Error executing bulk reassignment:', error)
      
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred'
      setError(errorMessage)
      setProcessingState('error')
      
      toast.current?.show({
        severity: 'error',
        summary: 'Reassignment Failed',
        detail: errorMessage,
        life: 8000
      })
      
    } finally {
      setIsProcessing(false)
    }
  }

  const resetState = () => {
    setIsProcessing(false)
    setProcessingState('idle')
    setReassignmentResults(null)
    setTransformedResults(null)
    setError(null)
  }

  return {
    // State
    isProcessing,
    processingState,
    reassignmentResults,
    apiResponse: reassignmentResults, // Alias for reassignmentResults
    transformedResults,
    toast,
    error,
    
    // Actions
    executeReassignment,
    resetState
  }
}